import angular from 'angular';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import SignalRService from '@/shared/services/SignalRService';

const ensureDate = value => {
  if (!value) return null;
  if (typeof value === 'string') return new moment(value.substr(0, 10)).toDate();
  if (moment.isMoment(value)) return value.toDate();
  if (value instanceof Date) return value;
  return value;
};
const mapDocument = ({
  createDate,
  documentDate,
  documentStartDate,
  documentEndDate,
  ...other
}) => ({
  ...other,
  createDate: ensureDate(createDate),
  documentDate: ensureDate(documentDate),
  documentStartDate: ensureDate(documentStartDate),
  documentEndDate: ensureDate(documentEndDate),
});

angular.module('cloudX').directive('cxFiscalDocumentList', function() {
  return {
    restrict: 'E',
    template: require('./fiscalDocumentList.html'),
    scope: {
      relId: '=',
      linkType: '=',
      linkId: '=',
      documents: '=?',
      onChange: '&',
      accessor: '=?',
    },
    bindToController: true,
    controllerAs: 'vm',
    controller: [
      '$scope',
      '$http',
      'appUrls',
      '$q',
      'flySvc',
      'ModalService',
      'dialogsSvc',
      '$timeout',
      function($scope, $http, appUrls, $q, flySvc, ModalService, dialogsSvc, $timeout) {
        const vm = this;
        vm.newDocuments = [];
        vm.showUploadSpinner = false;

        vm.triggerChanged = function() {
          vm.onChange({ $documents: vm.documents });
        };

        vm.sendDeleteDocumentRequest = function(documentId) {
          return $http.delete(`${appUrls.documents}/api/documents/${documentId}`);
        };
        vm.sendDeleteDocumentLinkRequest = function(documentId) {
          if (!vm.linkId || !vm.linkType) return $q.reject();

          return $http.delete(
            `${appUrls.documents}/api/documents/Links/${documentId}/${vm.linkId}/${vm.linkType}`,
          );
        };

        vm.onPreviewClosed = () => {
          $timeout(function() {
            vm.showPreview = false;
            vm.activeDocumentId = null;
          }, 0);
        };

        vm.onPreviewSaved = document => {
          $timeout(function() {
            vm.showPreview = false;
            vm.activeDocument = null;
            vm.documents = vm.documents.map(x => {
              if (x.id === document.id) {
                return {
                  ...x,
                  ...document,
                };
              }
              return x;
            });
          }, 0);
        };

        vm.previewDocument = document => {
          vm.activeDocument = document;
          vm.showPreview = true;
        };

        vm.deleteDocument = function(document, isNew) {
          if (isNew) {
            const idx = vm.newDocuments.indexOf(document);
            if (idx >= 0) vm.newDocuments.splice(idx, 1);
            return;
          } else {
            return dialogsSvc
              .showYesNoCancel('comfirmDeleteTitle', 'msgConfirmDeleteDocumentOrLink')
              .then(function(confirm) {
                if (confirm === null) return false; //cancel
                document.showSpinner = true;
                const request = confirm
                  ? vm.sendDeleteDocumentRequest(document.id)
                  : vm.sendDeleteDocumentLinkRequest(document.id);

                return request
                  .then(function(response) {
                    var result = response.data;
                    flySvc.success();
                    var index = vm.documents.indexOf(document);
                    if (index >= 0) vm.documents.splice(index, 1);
                    document.showSpinner = false;
                    vm.triggerChanged();
                  })
                  .catch(function(error) {
                    flySvc.error(error);
                    document.showSpinner = false;
                    return '';
                  });
              });
          }
        };

        vm.uploadDocuments = function(files, isSaveNew = false) {
          if (!files || files.length < 1) return $q.when(false);
          if (!vm.linkId) {
            if (isSaveNew) return $q.reject();
            files.forEach(x => {
              vm.newDocuments.push({
                ...x,
                documentType: null,
              });
            });
            return $q.when();
          }
          vm.showUploadSpinner = true;

          var filesPerDocumentType = files.reduce((o, file) => {
            const documentType = file.documentType ?? -1;

            let item = o.find(x => x.documentType === documentType);
            if (!item) {
              item = {
                documentType,
                files: [],
              };
              o.push(item);
            }
            item.files.push(file);
            return o;
          }, []);
          const requests = filesPerDocumentType.map(x => {
            const formData = new FormData();
            formData.append('relationId', vm.relId);
            formData.append('documentLinkType', vm.linkType);
            formData.append('linkId', vm.linkId);
            formData.append('documentType', x.documentType);

            x.files.forEach(function(file) {
              formData.append('data', file.data);
            });

            return $http({
              method: 'POST',
              url: appUrls.documents + '/api/documents',
              headers: {
                'Content-Type': undefined,
              },
              data: formData,
            });
          });
          return $q.all(requests).then(
            result => {
              if (!result) return;
              const documents = result.flatMap(({ data = [] }) => data);
              if (!vm.documents) vm.documents = [];
              if (isSaveNew) {
                files.forEach(function(file) {
                  const idx = vm.newDocuments.indexOf(file);
                  if (idx >= 0) vm.newDocuments.splice(idx, 1);
                });
              }

              documents.forEach(function(file) {
                vm.documents.push(mapDocument(file));
              });
              vm.triggerChanged();

              if (!isSaveNew) flySvc.success();

              vm.showUploadSpinner = false;
              return true;
            },
            error => {
              flySvc.error(error);
              vm.showUploadSpinner = false;
              return '';
            },
          );
        };

        vm.uploadNewDocuments = function(linkId) {
          if (!vm.newDocuments || vm.newDocuments.length <= 0) return $q.when(false);
          vm.linkId = linkId || vm.linkId;
          return vm.uploadDocuments(vm.newDocuments, true);
        };
        vm.updateDocument = function(document, field, value) {
          if (typeof value === 'undefined') return '';
          if (field === 'documentDate' && !value) return ''; //Document date is required

          var model = angular.copy(document);
          if (angular.equals(model[field], value)) return true;
          model[field] = value;
          if (field === 'documentType' && typeof value === 'string')
            model[field] = parseInt(newType);

          if (model.documentDate instanceof Date) model.documentDate = model.documentDate.ISO();
          if (model.documentStartDate instanceof Date)
            model.documentStartDate = model.documentStartDate.ISO();
          if (model.documentEndDate instanceof Date)
            model.documentEndDate = model.documentEndDate.ISO();

          return $http.put(appUrls.documents + '/api/Documents/Update', model).then(
            function(result) {
              if (result && result.data) {
                angular.extend(document, mapDocument(result.data));
                flySvc.success();
              }
              return false;
            },
            function(error) {
              flySvc.error(error);
              return '';
            },
          );
        };

        $scope.$watch('vm.accessor', function() {
          if (vm.accessor) {
            vm.accessor.uploadNewDocuments = vm.uploadNewDocuments;
          }
        });
        $scope.$watch('vm.documents', function() {
          if (!vm.documents) return;
          vm.documents.forEach(x => angular.extend(x, mapDocument(x)));
        });
        const documentUpdatedHandler = document => {
          if (!document) return;
          $timeout(function() {
            var doc = vm.documents.find(x => x.id == document.id);
            if (!doc) return;

            angular.extend(doc, mapDocument(document));
          }, 0);
        };
        SignalRService.subscribe('documentUpdated', documentUpdatedHandler);
        $scope.$on('$destroy', function() {
          SignalRService.unsubscribe('documentUpdated', documentUpdatedHandler);
        });
      },
    ],
  };
});
